import { InputAdornment, MenuItem, Select, TextField, TextFieldProps, Typography } from '@mui/material';
import { IconButtonDelete } from 'components/iconButtons';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { useCountryCode } from 'hooks/useCountryCode';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CountryData, defaultCountries, FlagImage, parseCountry, usePhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';

interface IProps {
    value: string;
    onChange: (value: string, formatted: string, isValid: boolean) => void;
    onClickDelete?: () => void;
}

type IMprcPhoneFieldProps = IProps & Omit<TextFieldProps, "slotProps" | "inputProps" | "onChange">;

const phoneUtil = PhoneNumberUtil.getInstance();

export const isPhoneValid = (phone: string) => {
    try {
        return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
    } catch {
        return false;
    }
};

export const MprcPhoneField: React.FC<IMprcPhoneFieldProps> = ({ value, onChange, onClickDelete, ...restProps }) => {

    const { countryCode, loading } = useCountryCode();
    const { t } = useTranslation("shared");

    const [focus, setFocus] = useState<boolean>(false);
    const [, setIsValid] = useState<boolean>(false);

    const countries = useMemo(() => defaultCountries.map((country) => {
        const parsed = parseCountry(country);

        if (parsed.iso2 === 'br') {
            return [
                'Brazil',
                'br',
                '55',
                {
                    default: '(..) ....-....',       // Fixo: DDD + 8 dígitos
                    '/^\\d{2}9/': '(..) .....-....', // Celular: DDD + começa com 9
                },
            ] as CountryData;
        }

        return country;
    }), [defaultCountries]);


    const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
        usePhoneInput({
            defaultCountry: countryCode.toLowerCase(),
            value: value,
            countries: countries,
            onChange: (data) => {
                const isValid = isPhoneValid(data.phone);
                setIsValid(isValid);
                onChange(data.phone, data.inputValue, isValid);
            },
        });


    return (
        <TextField
            disabled={loading}
            color="primary"
            variant="standard"
            value={inputValue}
            onChange={handlePhoneValueChange}
            type="tel"
            inputRef={inputRef}
            onFocus={() => setFocus(true)}
            onBlur={() => {
                setTimeout(() => setFocus(false), 300);
            }}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment
                            position="start"
                            style={{ marginRight: '2px', marginLeft: '-8px' }}
                        >
                            <Select
                                disableUnderline
                                MenuProps={{
                                    style: {
                                        height: '300px',
                                        width: '360px',
                                        top: '10px',
                                        left: '-34px',
                                    },
                                    transformOrigin: {
                                        vertical: 'top',
                                        horizontal: 'left',
                                    },
                                }}
                                sx={{
                                    width: 'max-content',
                                    // Remove default outline (display only on focus)
                                    fieldset: {
                                        display: 'none',
                                    },
                                    '&.Mui-focused:has(div[aria-expanded="false"])': {
                                        fieldset: {
                                            display: 'block',
                                        },
                                    },
                                    // Update default spacing
                                    '.MuiSelect-select': {
                                        padding: '8px',
                                        paddingRight: '24px !important',
                                    },
                                    svg: {
                                        right: 0,
                                    },
                                }}
                                value={country.iso2}
                                onChange={(e) => setCountry(e.target.value)}
                                renderValue={(value) => (
                                    <FlagImage iso2={value} style={{ display: 'flex' }} />
                                )}
                            >
                                {defaultCountries.map((c) => {
                                    const country = parseCountry(c);
                                    return (
                                        <MenuItem key={country.iso2} value={country.iso2}>
                                            <FlagImage
                                                iso2={country.iso2}
                                                style={{ marginRight: '8px' }}
                                            />
                                            <Typography marginRight="8px">{t("Country." + country.iso2.toUpperCase())}</Typography>
                                            <Typography color="gray">+{country.dialCode}</Typography>
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </InputAdornment>
                    ),
                    endAdornment: focus && onClickDelete != null ? <IconButtonDelete size="small" onClick={onClickDelete} /> : undefined
                }
            }}
            {...restProps}
        />
    );
};
